/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
    class VariantPicker extends HTMLElement {
        constructor() {
            super();
            this.section = this.closest('.js-product');
            this.productForm = this.section.querySelector('.js-product-form-main');
            this.optionSelectors = this.querySelectorAll('.option-selector');
            this.data = this.getProductData();

            // Ensure DOM is ready before initializing
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', () => this.initializeVariant());
            } else {
                // Use a small delay to ensure all dynamic content is loaded
                setTimeout(() => this.initializeVariant(), 50);
            }

            // Set up mutation observer to watch for dynamic content changes
            this.setupMutationObserver();

            this.addEventListener('change', this.handleVariantChange.bind(this));
        }

        /**
         * Sets up a mutation observer to watch for dynamic content changes
         */
        setupMutationObserver() {
            if (!this.section) return;

            const observer = new MutationObserver((mutations) => {
                let shouldReinitialize = false;

                mutations.forEach((mutation) => {
                    if (mutation.type === 'childList') {
                        mutation.addedNodes.forEach((node) => {
                            if (node.nodeType === Node.ELEMENT_NODE) {
                                // Check if variant elements were added
                                if (node.querySelector && (
                                        node.querySelector('.product_swatch--block .product_variant--item') ||
                                        node.classList.contains('product_swatch--block') ||
                                        node.classList.contains('product_variant--item')
                                    )) {
                                    shouldReinitialize = true;
                                }
                            }
                        });
                    }
                });

                if (shouldReinitialize && this.variant) {
                    // Re-initialize after a short delay to ensure DOM is stable
                    setTimeout(() => {
                        this.updateAddToCartButton();
                    }, 100);
                }
            });

            observer.observe(this.section, {
                childList: true,
                subtree: true
            });

            // Store observer reference for cleanup
            this.mutationObserver = observer;
        }

        /**
         * Initializes the variant and updates all UI states
         */
        initializeVariant() {
            // Initialize the variant based on current selection
            const selectedOptions = this.getSelectedOptions();
            this.variant = this.data.product.variants.find((v) =>
                v.options.every((val, index) => val === selectedOptions[index])
            );

            // If no variant found initially, try to find the first available variant or wait for DOM
            if (!this.variant) {
                // Try to find a variant that matches the URL variant parameter
                const urlParams = new URLSearchParams(window.location.search);
                const variantId = urlParams.get('variant');
                if (variantId) {
                    this.variant = this.data.product.variants.find(v => v.id.toString() === variantId);
                }

                // If still no variant, use the first one as fallback
                if (!this.variant && this.data.product.variants.length > 0) {
                    this.variant = this.data.product.variants[0];
                }
            }

            // Initialize all UI states for the selected variant
            if (this.variant) {
                this.ensureVariantElementAttributes();
                this.updateVariantLabels();
                this.updateSubscribeButton();
                this.updateAddToCartButton();
                this.updateAvailability();
                this.updatePrice();
                this.updateWeight();
                this.updateBarcode();
                this.updateBackorderText();
                this.updatePickupAvailability();
                this.updateSku();

                // Ensure media and forms reflect the initial / URL-selected variant
                this.updateMedia();
                this.updateVariantInput();

                // Notify any listeners (e.g. media-gallery) about the initial variant
                this.dispatchEvent(new CustomEvent('on:variant:change', {
                    bubbles: true,
                    detail: {
                        form: this.productForm,
                        variant: this.variant,
                        product: this.data.product
                    }
                }));

                // Re-run media sync after layout settles so thumbs can scroll correctly on first load
                setTimeout(() => {
                    if (this.variant) {
                        this.updateMedia();
                    }
                }, 250);

                // If the add to cart button update failed (variant element not found), retry after a short delay
                // This handles cases where DOM elements are generated dynamically
                setTimeout(() => {
                    if (this.variant) {
                        this.updateAddToCartButton();
                    }
                }, 100);

                // Additional retry with longer delay for cases where elements take longer to load
                setTimeout(() => {
                    if (this.variant) {
                        this.ensureVariantElementAttributes();
                        this.updateAddToCartButton();
                    }
                }, 500);
            }
        }

        /**
         * Handles 'change' events on the variant picker element.
         * @param {object} evt - Event object.
         */
        handleVariantChange(evt) {
            const selectedOptions = this.getSelectedOptions();
            this.variant = null;

            // Get selected variant data (if variant exists).
            this.variant = this.data.product.variants.find((v) =>
                v.options.every((val, index) => val === selectedOptions[index])
            );

            if (this.variant) {
                this.updateVariantLabels();
                // this.updateVariantPreOrder();
                this.updateMedia();
                this.updateUrl(evt);
                this.updateVariantInput();
            }
            this.updateSubscribeButton();
            this.updateAddToCartButton();
            this.updateAvailability();
            this.updatePrice();
            this.updateWeight();
            this.updateBarcode();
            this.updateBackorderText();
            this.updatePickupAvailability();
            this.updateSku();
            VariantPicker.updateLabelText(evt);

            this.dispatchEvent(new CustomEvent('on:variant:change', {
                bubbles: true,
                detail: {
                    form: this.productForm,
                    variant: this.variant,
                    product: this.data.product
                }
            }));
        }

        updateVariantLabels() {
            const customLabelsDOM = document.getElementById("custom_product_labels");
            if (customLabelsDOM && this.variant) {
                const variantDataDivs = customLabelsDOM.querySelectorAll(".variant_data");
                variantDataDivs.forEach(div => div.classList.add("hidden"));
                // Match the exact case used in HTML: data-variantID
                const selectedVariantDiv = customLabelsDOM.querySelector(`.variant_data[data-variantID="${this.variant.id}"]`);
                if (selectedVariantDiv) {
                    selectedVariantDiv.classList.remove("hidden");
                }
            }
        }

        updateVariantPreOrder() {
            const customLabelsDOM = document.getElementById("product_pre_order");
            if (customLabelsDOM) {
                const variantDataDivs = customLabelsDOM.querySelectorAll(".variant_preorder_data");
                variantDataDivs.forEach(div => div.classList.add("hidden"));
                const selectedVariantDiv = customLabelsDOM.querySelector(`.variant_preorder_data[data-variantid="${this.variant.id}"]`);
                if (selectedVariantDiv) {
                    selectedVariantDiv.classList.remove("hidden");
                }
            }
        }

        updateSubscribeButton() {
            let currentVariant = this.variant.id;
            let productInfo = this.closest(".product-info");
            if (productInfo) {
                productInfo.querySelectorAll(".subcribe-text").forEach((subscribeLink) => {
                    let variantId = subscribeLink.getAttribute("data-variant-id");
                    // console.log(variantId);
                    if (variantId == currentVariant) {
                        subscribeLink.removeAttribute("hidden", "hidden");
                    } else {
                        subscribeLink.setAttribute("hidden", "hidden");
                    }
                });
            }
        }

        /**
         * Updates the "Add to Cart" button label and disabled state.
         */
        updateAddToCartButton() {
            if (!this.section) {
                // this.section is required for button updates; abort if missing
                console.error("VariantPicker: this.section is undefined");
                return;
            }

            this.productForm = this.section.querySelector('.js-product-form-main');
            if (!this.productForm) return;

            this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');

            if (!this.variant) {
                console.error("VariantPicker: variant is not defined");
                return;
            }

            const variantAvailable = this.variant && this.variant.available;
            const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;

            if (this.addBtn) {
                this.addBtn.disabled = !variantAvailable;
                this.addBtn.textContent = variantAvailable ?
                    this.addBtn.dataset.addToCartText :
                    unavailableStr;
            }

            const currentVariantName = this.variant.title || "";
            const currentVariantId = this.variant.id;

            // Try to find variant element by ID first (more reliable), then by title
            let currentVariantElement = null;

            // First try to find by variant ID - scope to this.section if available
            const searchContainer = this.section || document;
            const variantItemsById = searchContainer.querySelectorAll(".product_swatch--block .product_variant--item[data-variant-id]");
            variantItemsById.forEach((el) => {
                if (el.getAttribute("data-variant-id") === currentVariantId.toString()) {
                    currentVariantElement = el;
                }
            });

            // If not found by ID, try by title (fallback)
            if (!currentVariantElement) {
                const variantItems = searchContainer.querySelectorAll(".product_swatch--block .product_variant--item");
                variantItems.forEach((el) => {
                    if (el.getAttribute("data-variant") === currentVariantName) {
                        currentVariantElement = el;
                    }
                });
            }

            // If still not found, try to find any variant element with the same data attributes
            if (!currentVariantElement) {
                const variantItems = searchContainer.querySelectorAll(".product_swatch--block .product_variant--item");
                variantItems.forEach((el) => {
                    const elVariantId = el.getAttribute("data-variant-id");
                    const elVariantTitle = el.getAttribute("data-variant");
                    if (elVariantId === currentVariantId.toString() || elVariantTitle === currentVariantName) {
                        currentVariantElement = el;
                    }
                });
            }

            if (!currentVariantElement) {
              console.warn("Variant element not found! Trying to use variant data directly...");
              // Fallback: use the variant data directly if element not found
              this.updateAddToCartButtonFallback();
              return;
            }

            const currentVariantQty = parseInt(currentVariantElement.getAttribute("data-qty") || "0", 10);
            const currentVariantPreLimit = parseInt(currentVariantElement.getAttribute("data-pre-limit") || "0", 10);
            const currentVariantPreOrder = currentVariantElement.getAttribute("data-pre-order") === "true";
            const currentVariantInventoryPolicies = currentVariantElement.getAttribute("data-inventory_policy");
            const currentVariantDiscard = currentVariantElement.getAttribute("data-discard-variant") === "true";
            const currentVariantInventoryManagement = currentVariantElement.getAttribute("data-variant-inventory");
            const addButton = document.querySelector(".product-info__add-button .added_to--cart");
            const restockButton = document.querySelector(".restock-alerts-notify-button");
            const preorderText = document.querySelector(".preorder_text");

            // Logs for debugging
            console.log({
                currentVariantQty,
                currentVariantPreOrder,
                currentVariantPreLimit,
                currentVariantInventoryPolicies,
                currentVariantDiscard
            });

            // Get language and set appropriate text
            const lang = document.documentElement.lang;
            const addToBagText = lang === "he" ? "הוספה לסל" : "Add to bag";
            const preOrderText = lang === "he" ? "הזמנה מראש" : "Pre order";
            const soldOutText = lang === "he" ? "אזל מהמלאי" : "Sold Out";

            // WHEN VARIANT IS NOT AVAILABLE
               if (currentVariantInventoryManagement === "") {
                if (addButton) {
                    addButton.textContent = addToBagText;
                    addButton.style.display = "block";
                }
                if (restockButton) restockButton.style.display = "none";
                if (preorderText) preorderText.style.display = "none";
            } else {
            if (currentVariantQty <= 0) {
                setTimeout(() => {
                    if (preorderText) preorderText.style.display = "none";

                    if (
                        currentVariantInventoryPolicies === 'continue' &&
                        currentVariantPreOrder &&
                        currentVariantQty <= 0 &&
                        currentVariantQty > -currentVariantPreLimit
                    ) {
                        console.log("preorder");
                        if (addButton) {
                            addButton.textContent = preOrderText;
                            addButton.style.display = "block";
                        }
                        if (restockButton) restockButton.style.display = "none";
                        if (preorderText) preorderText.style.display = "block";
                    } else {
                        if (addButton) addButton.textContent = soldOutText;

                        if (
                            currentVariantDiscard === true &&
                            currentVariantQty === 0 &&
                            currentVariantInventoryPolicies !== 'continue'
                        ) {
                            // Hide add button, show restock button
                            if (addButton) {
                                addButton.style.display = "none";
                            }

                            setTimeout(function() {
                                if (restockButton) {
                                    restockButton.style.display = "block";
                                }
                            }, 500);
                        } else {
                            // Show add button, hide restock button (fallback)
                            if (addButton) {
                                addButton.style.display = "block";
                            }

                            setTimeout(function() {
                                if (restockButton) {
                                    restockButton.style.display = "none";
                                }
                            }, 500);
                        }

                    }
                }, 500);
            } else {
                setTimeout(() => {
                    if (addButton) {
                        addButton.textContent = addToBagText;
                        addButton.style.display = "block";
                    }
                    if (restockButton) restockButton.style.display = "none";
                }, 200);
            }
        }
            const currentVariantPrice = parseFloat(this.variant.price || 0);
            const currentVariantComparePrice = parseFloat(this.variant.compare_at_price || 0);

            const discountPercentage = currentVariantComparePrice > 0 ?
                Math.round(((currentVariantComparePrice - currentVariantPrice) / currentVariantComparePrice) * 100) :
                0;

            const newINScript = document.querySelector('#variant_new_in_data');
            let newINData = {};

            if (newINScript && newINScript.textContent.trim()) {
                try {
                    newINData = JSON.parse(newINScript.textContent.trim());
                } catch (error) {
                    console.error("VariantPicker: error parsing newIN JSON data:", error);
                }
            }

            const newINStatus = newINData[`variant:${currentVariantId}`];

            setTimeout(() => {
                document.querySelectorAll(".media-gallery .product-label.product-label--sale").forEach((element) => {
                    if (currentVariantComparePrice > currentVariantPrice) {
                        element.removeAttribute('hidden');
                        element.style.removeProperty('display');
                        element.textContent = `${element.dataset.saleLabelText} ${discountPercentage}%`;
                    } else {
                        element.style.display = "none";
                    }
                });

                document.querySelectorAll(".media-gallery .variant-label").forEach((element) => {
                    if (newINStatus === true) {
                        element.classList.remove('hide');
                    } else {
                        element.classList.add('hide');
                    }
                });
            }, 500);
        }

        /**
         * Fallback method to update add to cart button when variant element is not found
         */
        updateAddToCartButtonFallback() {
            if (!this.variant) return;

            const addButton = document.querySelector(".product-info__add-button .added_to--cart");
            const restockButton = document.querySelector(".restock-alerts-notify-button");
            const preorderText = document.querySelector(".preorder_text");

            // Get language and set appropriate text
            const lang = document.documentElement.lang;
            const addToBagText = lang === "he" ? "הוספה לסל" : "Add to bag";
            const preOrderText = lang === "he" ? "הזמנה מראש" : "Pre order";
            const soldOutText = lang === "he" ? "אזל מהמלאי" : "Sold Out";

            // Use variant data directly to determine button state
            const variantAvailable = this.variant.available;
            const variantInventoryPolicy = this.variant.inventory_policy;

            if (variantAvailable) {
                if (addButton) {
                    addButton.textContent = addToBagText;
                    addButton.style.display = "block";
                }
                if (restockButton) restockButton.style.display = "none";
                if (preorderText) preorderText.style.display = "none";
            } else {
                // Check if this is a pre-order variant
                if (variantInventoryPolicy === 'continue') {
                    if (addButton) {
                        addButton.textContent = preOrderText;
                        addButton.style.display = "block";
                    }
                    if (restockButton) restockButton.style.display = "none";
                    if (preorderText) preorderText.style.display = "block";
                } else {
                    if (addButton) {
                        addButton.textContent = soldOutText;
                        addButton.style.display = "block";
                    }
                    if (restockButton) restockButton.style.display = "none";
                    if (preorderText) preorderText.style.display = "none";
                }
            }
        }

        /**
         * Ensures variant elements have the correct data attributes
         */
        ensureVariantElementAttributes() {
            if (!this.variant) return;

            const variantElements = document.querySelectorAll(".product_swatch--block .product_variant--item");
            variantElements.forEach((el) => {
                // Ensure variant ID attribute is set
                if (!el.hasAttribute('data-variant-id')) {
                    const variantTitle = el.getAttribute('data-variant');
                    if (variantTitle) {
                        // Find the variant by title and set the ID
                        const matchingVariant = this.data.product.variants.find(v => v.title === variantTitle);
                        if (matchingVariant) {
                            el.setAttribute('data-variant-id', matchingVariant.id.toString());
                        }
                    }
                }
            });
        }

        /**
         * Updates the availability status in option selectors.
         */
        updateAvailability() {
            if (this.dataset.showAvailability === 'false') return;
            let currVariant = this.variant;

            if (!this.variant) {
                currVariant = {
                    options: this.getSelectedOptions()
                };
            }

            const updateOptionAvailability = (optionEl, available, soldout) => {

                const el = optionEl;
                // console.log(el);
                const text = soldout ? theme.strings.noStock : theme.strings.noVariant;
                el.classList.toggle('is-unavailable', !available);
                if (optionEl.classList.contains('custom-select__option')) {
                    const em = el.querySelector('em');

                    if (em) {
                        em.hidden = available;
                    }

                    if (!available) {
                        if (em) {
                            em.textContent = text;
                        } else {
                            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${text}</em>`;
                        }
                    }
                } else if (!available) {
                    el.nextElementSibling.title = text;
                    // soldOutLabel.classList.remove('hidden');

                } else {
                    el.nextElementSibling.removeAttribute('title');
                    // soldOutLabel.classList.add('hidden');

                }
            };

            // Check if the product has color variants.
            // Do not rely only on English option names because Hebrew stores use "צבע".
            const firstSelector = this.optionSelectors.length > 0 ? this.optionSelectors[0] : null;
            const firstSelectorName = firstSelector && firstSelector.dataset && firstSelector.dataset.name ?
                firstSelector.dataset.name.toLowerCase().trim() :
                '';
            const isKnownColorName = ['color', 'colour', 'צבע'].includes(firstSelectorName);
            const hasSwatchInputs = firstSelector ? !!firstSelector.querySelector('[data-swatch], .opt-label--swatch') : false;
            const hasColorOption = this.optionSelectors.length > 0 && (isKnownColorName || hasSwatchInputs);
            let allSizeUnavailable = true;

            // If the product has a color option, handle the color availability logic
            if (hasColorOption && this.optionSelectors.length > 1) {
                // Get the selected color option
                const selectedColor = currVariant.options[0]; // Assuming the first option is color

                // Flag selector options as available or sold out, depending on the variant availability
                this.optionSelectors.forEach((selector, selectorIndex) => {
                    this.data.product.variants.forEach((variant) => {
                        let matchCount = 0;

                        variant.options.forEach((option, optionIndex) => {
                            if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                                matchCount += 1;
                            }
                        });

                        if (matchCount === currVariant.options.length - 1 && variant.options[0] === selectedColor) {
                            const options = selector.querySelectorAll('.js-option');
                            const optionEl = Array.from(options).find((opt) => {
                                if (selector.dataset.selectorType === 'dropdown') {
                                    return opt.dataset.value === variant.options[selectorIndex];
                                }
                                return opt.value === variant.options[selectorIndex];
                            });

                            if (optionEl) {
                                const variantAvailable = variant.available;
                                console.log(variantAvailable);
                                updateOptionAvailability(optionEl, variantAvailable, !variantAvailable);

                                // Check if this is a size option and update the allSizeUnavailable flag
                                if (selectorIndex === 1 && variantAvailable) { // Assuming index 1 is for size
                                    allSizeUnavailable = false;
                                }
                            }
                        }
                    });
                });

                // If all sizes for the selected color are unavailable, mark the selected color as unavailable
                const colorSelector = this.optionSelectors[0]; // Assuming index 0 is for color
                const selectedColorOptionEl = Array.from(colorSelector.querySelectorAll('.js-option')).find(
                    (optionEl) => optionEl.value === selectedColor
                );

                if (selectedColorOptionEl) {
                    updateOptionAvailability(selectedColorOptionEl, !allSizeUnavailable, allSizeUnavailable);
                }
            } else {
                // If no color option exists, just handle availability for other options (e.g., size)
                this.optionSelectors.forEach((selector, selectorIndex) => {
                    this.data.product.variants.forEach((variant) => {
                        const options = selector.querySelectorAll('.js-option');
                        const optionEl = Array.from(options).find((opt) => {
                            return opt.value === variant.options[selectorIndex];
                        });

                        if (optionEl) {
                            const variantAvailable = variant.available;
                            updateOptionAvailability(optionEl, variantAvailable, !variantAvailable);
                        }
                    });
                });
            }

            // Remove hidden class from .sold-out-label if all sizes are sold out for the selected color
            // document.querySelectorAll(".option-selector.Color label").forEach((selectedColor) => {
            //   console.log(selectedColor);
            //   if (selectedColor.getAttribute('title') === "Sold out") {
            //     soldOutLabel.classList.remove('hidden');
            //   } else {
            //     soldOutLabel.classList.add('hidden');
            //   }
            // });
        }






        /**
         * Updates the backorder text and visibility.
         */
        updateBackorderText() {
            this.backorder = this.backorder || this.section.querySelector('.backorder');
            if (!this.backorder) return;

            let hideBackorder = true;

            if (this.variant && this.variant.available) {
                const {
                    inventory
                } = this.data.formatted[this.variant.id];

                if (this.variant.inventory_management && inventory === 'none') {
                    const backorderProdEl = this.backorder.querySelector('.backorder__product');
                    const prodTitleEl = this.section.querySelector('.product-title');
                    const variantTitle = this.variant.title.includes('Default') ?
                        '' :
                        ` - ${this.variant.title}`;

                    backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
                    hideBackorder = false;
                }
            }

            this.backorder.hidden = hideBackorder;
        }

        /**
         * Updates the color option label text.
         * @param {object} evt - Event object
         */
        static updateLabelText(evt) {
            const selector = evt.target.closest('.option-selector');
            if (selector.dataset.selectorType === 'dropdown') return;

            const colorText = selector.querySelector('.js-color-text');
            if (!colorText) return;

            colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
        }

        /**
         * Updates the product media.
         */
        updateMedia() {
            if (!this.variant || !this.variant.featured_media) return;

            // In the Quick Add drawer, media is handled by quick-add.js
            if (this.section && this.section.matches('quick-add-drawer')) {
                return;
            }

            if (!this.section) return;

            // On the PDP, explicitly tell the media gallery to activate this variant's media
            this.mediaGallery = this.mediaGallery || (this.section && this.section.querySelector('media-gallery'));
            if (!this.mediaGallery) return;

            const variantMedia = this.mediaGallery.querySelector(
                `[data-media-id="${this.variant.featured_media.id}"]`
            );
            if (!variantMedia) return;

            if (typeof this.mediaGallery.customSetActiveMedia === 'function') {
                this.mediaGallery.customSetActiveMedia(variantMedia, true);
            } else if (typeof this.mediaGallery.setActiveMedia === 'function') {
                // Backwards compatibility with older themes
                this.mediaGallery.setActiveMedia(variantMedia, true, true);
            } else {
                // Manual fallback: update classes and scroll so the correct image is shown
                const viewer = this.mediaGallery.querySelector('.media-viewer');
                if (!viewer) return;

                const items = viewer.querySelectorAll('.media-viewer__item');
                items.forEach((item) => {
                    item.classList.remove('active', 'is-current-variant');
                });

                variantMedia.classList.add('active', 'is-current-variant');

                // Scroll the viewer so the variant image is in view
                if (viewer.scrollTo && typeof variantMedia.offsetLeft === 'number') {
                    viewer.scrollTo({ left: variantMedia.offsetLeft, behavior: 'smooth' });
                }

                // Try to update dots if present
                const dotsContainer = this.mediaGallery.querySelector('.dots-btn');
                if (dotsContainer) {
                    const dots = dotsContainer.querySelectorAll('.dotbtn');
                    const index = Array.prototype.indexOf.call(items, variantMedia);
                    if (index >= 0) {
                        dots.forEach((dot, i) => {
                            dot.classList.toggle('active', i === index);
                        });
                    }
                }

                // Also sync thumbnails and ensure the active thumb is visible
                const thumbsContainer = this.mediaGallery.querySelector('.media-thumbs');
                if (thumbsContainer) {
                    const thumbItems = thumbsContainer.querySelectorAll('.media-thumbs__item');
                    const targetThumb = Array.prototype.find.call(
                        thumbItems,
                        (thumb) => thumb.getAttribute('data-media-id') === String(this.variant.featured_media.id)
                    );

                    const thumbButtons = thumbsContainer.querySelectorAll('.media-thumbs__btn');
                    thumbButtons.forEach((btn) => {
                        btn.classList.remove('is-active');
                        btn.removeAttribute('aria-current');
                    });

                    if (targetThumb) {
                        const btn = targetThumb.querySelector('.media-thumbs__btn');
                        if (btn) {
                            // Mark as active
                            btn.classList.add('is-active');
                            btn.setAttribute('aria-current', 'true');

                            // Also trigger the thumbnail's click handler so media-gallery
                            // can run any additional logic it has (e.g. zoom, video setup).
                            if (typeof btn.click === 'function') {
                                btn.click();
                            }
                        }

                        // Scroll thumbs so the active thumb is in view.
                        // Use bounding rects to compute relative delta inside the scroll container.
                        const containerRect = thumbsContainer.getBoundingClientRect();
                        const thumbRect = targetThumb.getBoundingClientRect();

                        // Detect vertical vs horizontal thumbs layout
                        const isVertical = containerRect.height > containerRect.width;

                        if (isVertical) {
                            const containerCenterY = containerRect.top + containerRect.height / 2;
                            const thumbCenterY = thumbRect.top + thumbRect.height / 2;
                            const deltaY = thumbCenterY - containerCenterY;
                            const newScrollTop = thumbsContainer.scrollTop + deltaY;

                            if (thumbsContainer.scrollTo) {
                                thumbsContainer.scrollTo({
                                    top: newScrollTop,
                                    behavior: 'smooth'
                                });
                            } else {
                                thumbsContainer.scrollTop = newScrollTop;
                            }

                        } else {
                            const containerCenterX = containerRect.left + containerRect.width / 2;
                            const thumbCenterX = thumbRect.left + thumbRect.width / 2;
                            const deltaX = thumbCenterX - containerCenterX;
                            const newScrollLeft = thumbsContainer.scrollLeft + deltaX;

                            if (thumbsContainer.scrollTo) {
                                thumbsContainer.scrollTo({
                                    left: newScrollLeft,
                                    behavior: 'smooth'
                                });
                            } else {
                                thumbsContainer.scrollLeft = newScrollLeft;
                            }

                        }
                    }
                }
            }
        }

        /**
         * Updates the pick up availability.
         */
        updatePickupAvailability() {
            this.pickUpAvailability =
                this.pickUpAvailability || this.section.querySelector('pickup-availability');
            if (!this.pickUpAvailability) return;

            if (this.variant && this.variant.available) {
                if (this.pickUpAvailability && typeof this.pickUpAvailability.getAvailability === 'function') {
                    this.pickUpAvailability.getAvailability(this.variant.id);
                }
            } else {
                this.pickUpAvailability.removeAttribute('available');
                this.pickUpAvailability.innerHTML = '';
            }
        }

        /**
         * Updates the price.
         */
        updatePrice() {
            this.price = this.price || this.section.querySelector('.product-info__price > .price');
            if (!this.price) return;

            if (this.variant) {
                const priceCurrentEl = this.price.querySelector('.price__current');
                const priceWasEl = this.price.querySelector('.price__was');
                const unitPriceEl = this.price.querySelector('.unit-price');

                // Update current price and original price if on sale.
                priceCurrentEl.innerHTML = this.data.formatted[this.variant.id].price;
                if (priceWasEl)
                    priceWasEl.innerHTML = this.data.formatted[this.variant.id].compareAtPrice || '';

                // Update unit price, if specified.
                if (this.variant.unit_price_measurement) {
                    const valueEl = this.price.querySelector('.unit-price__price');
                    const unitEl = this.price.querySelector('.unit-price__unit');
                    const value = this.variant.unit_price_measurement.reference_value;
                    const unit = this.variant.unit_price_measurement.reference_unit;

                    valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
                    unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
                }

                unitPriceEl.hidden = !this.variant.unit_price_measurement;
                this.price.classList.toggle(
                    'price--on-sale',
                    this.variant.compare_at_price > this.variant.price
                );
                this.price.classList.toggle('price--sold-out', !this.variant.available);
            }

            this.price.querySelector('.price__default').hidden = !this.variant;
            this.price.querySelector('.price__no-variant').hidden = this.variant;
        }

        /**
         * Updates the weight.
         */
        updateWeight() {
            this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
            if (this.weights.length === 0) return;

            const weightAvailable = this.variant && this.variant.weight > 0;
            this.weights.forEach((weight) => {
                weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
                weight.hidden = !weightAvailable;
            });
        }

        /**
         * Updates the Barcode.
         */
        updateBarcode() {
            this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
            if (this.barcodes.length === 0) return;

            const barcodeAvailable = this.variant && this.variant.barcode;
            this.barcodes.forEach((barcode) => {
                barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
                barcode.parentNode.hidden = !barcodeAvailable;
            });
        }

        /**
         * Updates the SKU.
         */
        updateSku() {
            this.sku = this.sku || this.section.querySelector('.product-sku__value');
            if (!this.sku) return;

            const skuAvailable = this.variant && this.variant.sku;
            this.sku.textContent = skuAvailable ? this.variant.sku : '';
            this.sku.parentNode.hidden = !skuAvailable;
        }

        /**
         * Updates the url with the selected variant id.
         * @param {object} evt - Event object.
         */
        updateUrl(evt) {
            if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
            window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
        }

        /**
         * Updates the value of the hidden [name="id"] form inputs.
         */
        updateVariantInput() {
            this.forms =
                this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

            this.forms.forEach((form) => {
                const input = form.querySelector('input[name="id"]');
                input.value = this.variant.id;
                input.dispatchEvent(new Event('change', {
                    bubbles: true
                }));
            });
        }

        /**
         * Gets the selected option element from each selector.
         * @returns {Array}
         */
        getSelectedOptions() {
            const selectedOptions = [];

            this.optionSelectors.forEach((selector) => {
                if (selector.dataset.selectorType === 'dropdown') {
                    const btn = selector.querySelector('.custom-select__btn');
                    if (btn) {
                        selectedOptions.push(btn.textContent.trim());
                    }
                } else {
                    const checkedInput = selector.querySelector('input:checked');
                    if (checkedInput) {
                        selectedOptions.push(checkedInput.value);
                    }
                }
            });

            return selectedOptions;
        }

        /**
         * Gets the product data.
         * @returns {?object}
         */
        getProductData() {
            const dataEl = this.querySelector('[type="application/json"]');
            return JSON.parse(dataEl.textContent);
        }

        /**
         * Cleanup when element is disconnected
         */
        disconnectedCallback() {
            if (this.mutationObserver) {
                this.mutationObserver.disconnect();
            }
        }
    }

    customElements.define('variant-picker', VariantPicker);
}